// Copyright © 2023 Move Closer
import { __decorate } from "tslib";
import { Component, Inject, Prop, Ref, Watch } from 'vue-property-decorator';
import { AbstractModuleUi } from '../../_abstract/ui';
import { AdBannerSize, AdBannerType } from '../AdBanner.contracts';
import { adBannerSizeClassRegistry, adBannerSizeCreativesDimensionsRegistry } from '../AdBanner.config';
/**
 * Component capable of rendering the `AdBannerModule's` contents.
 *
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 */
let AbstractAdBanner = class AbstractAdBanner extends AbstractModuleUi {
    constructor() {
        super(...arguments);
        /**
         * ID of the HTML element that will host the ad.
         */
        this.elementId = '';
        this.bannerType = '';
        this.isEmpty = true;
        this.offscreen = false;
        this.resolvedContainerStyle = null;
        this.resolvedSectionStyle = null;
        this.hide = false;
    }
    /**
     * Ths CSS class defining the ad banner's size (aspect ratio, in fact).
     */
    get bannerSizeClass() {
        return adBannerSizeClassRegistry[this.module.content.size];
    }
    /**
     * Ths CSS class defining the ad banner's size (aspect ratio, in fact).
     */
    get isDarkHeader() {
        return [AdBannerSize.StickyTeaser, AdBannerSize.StickyFixed].includes(this.module.content.size);
    }
    /**
     * Resolve ad banner's version.
     */
    get bannerVersionClass() {
        switch (this.module.content.type) {
            case AdBannerType.StickyFixed:
                return 'ad-banner__sticky_fixed';
            case AdBannerType.Sticky:
                return 'ad-banner__sticky';
            case AdBannerType.StickyTop:
                return 'ad-banner__sticky-top';
            default:
                return '';
        }
    }
    /**
     * Check if content has defined different ad size for mobile
     */
    get hasDifferentMobileSize() {
        return !!this.module.content.mobileSize && Array.isArray(this.module.content.mobileSize) &&
            Object.values(AdBannerSize).includes(this.module.content.mobileSize);
    }
    get shouldShrink() {
        if (typeof this.module.content.doNotShrink === 'undefined') {
            this.module.content.doNotShrink = true;
        }
        if (typeof this.module.content.forceShrink === 'undefined') {
            this.module.content.forceShrink = false;
        }
        return !this.module.content.doNotShrink || this.module.content.forceShrink;
    }
    get shouldDisplay() {
        return this.hasValidConfig;
    }
    get hasValidConfig() {
        return true;
    }
    get showCloseButton() {
        return this.bannerType === AdBannerType.StickyFixed || this.bannerType === AdBannerType.StickyTeaser;
    }
    toggleHide(e) {
        e.preventDefault();
        e.stopPropagation();
        this.hide = !this.hide;
    }
    /**
     * Creative's dimensions for the given `AdBannerSize`.
     */
    get creativesDimensions() {
        let sizes;
        // new banner grid has sizes synced with GAM and the checkbox for different size on mobile should be ignored
        if (this.module.content.sizes && this.module.content.sizes.length > 0) {
            sizes = this.module.content.sizes;
        }
        else if (this.hasDifferentMobileSize && this.isMobile) { // for old banner grid check for mobile sizes
            // In method hasDifferentMobileSize we check if `this.module.content.mobileSize` exist,
            // but TS doesn't want to believe that's why `!`.
            sizes = adBannerSizeCreativesDimensionsRegistry[this.module.content.mobileSize];
        }
        else {
            sizes = adBannerSizeCreativesDimensionsRegistry[this.module.content.size];
        }
        // on mobile filter out dimensions with width larger than 300
        if (this.isMobile) {
            return sizes.filter((size) => {
                return size[0] <= 300 && size[1] <= 300;
            });
        }
        return sizes;
    }
    changeSectionStyl(newWidth) {
        this.resolvedSectionStyle = {
            maxWidth: `${newWidth}px`,
            marginLeft: 'auto',
            marginRight: 'auto'
        };
    }
    changeContainerStyle(newPadding) {
        this.resolvedContainerStyle = {
            paddingTop: `${newPadding}%`
        };
    }
    /**
     * Sets the value of `this.elementId`.
     */
    setElementId() {
        this.elementId = this.$id('ad-banner');
    }
    /**
     * Sets the value of `this.elementId`.
     */
    setBannerType() {
        this.bannerType = this.module.content.type;
        if (this.bannerType === AdBannerType.StickyTeaser) {
        }
    }
    static roundRatio(height, width, tolerance = 1000) {
        return Math.round(height / width * tolerance) / tolerance;
    }
    initStickyTopAd() {
        var _a;
        document.documentElement.style.setProperty('--sticky-top-ad-height', ((_a = this.bannerContainer) === null || _a === void 0 ? void 0 : _a.clientHeight) + 'px');
    }
    initStickyBottomAd() {
        this.onScroll();
        window.addEventListener('scroll', this.onScroll, { passive: true });
    }
    onScroll() {
        const stickyTopAdHeight = document.documentElement.style.getPropertyValue('--sticky-top-ad-height');
        const offset = stickyTopAdHeight ? parseInt(stickyTopAdHeight, 10) : 0;
        this.offscreen = (window.pageYOffset || document.documentElement.scrollTop) < offset;
    }
    onContentChange() {
        if (!this.isEmpty) {
            // if (this.module.content.size === AdBannerSize.StickyTop) {
            //   this.initStickyTopAd()
            // }
            if (this.module.content.size === AdBannerSize.StickyFixed) {
                this.initStickyBottomAd();
            }
        }
    }
};
__decorate([
    Inject('onEmpty')
], AbstractAdBanner.prototype, "onEmpty", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: false
    })
], AbstractAdBanner.prototype, "forceHide", void 0);
__decorate([
    Prop({
        type: Function,
        required: false
    })
], AbstractAdBanner.prototype, "onEmptyCallback", void 0);
__decorate([
    Ref('container')
], AbstractAdBanner.prototype, "bannerContainer", void 0);
__decorate([
    Watch('isEmpty')
], AbstractAdBanner.prototype, "onContentChange", null);
AbstractAdBanner = __decorate([
    Component({
        name: 'AbstractAdBanner',
        components: {},
        mounted() {
            this.setElementId();
            this.setBannerType();
            this.onContentChange();
        },
        beforeDestroy() {
            // if (this.module.content.size === AdBannerSize.StickyTop) {
            //   document.documentElement.style.removeProperty('--sticky-top-ad-height')
            // }
            if (this.module.content.size === AdBannerSize.StickyFixed) {
                window.removeEventListener('scroll', this.onScroll);
            }
        },
        template: `
    <section v-if="shouldDisplay && !hide && !forceHide" class="ad-banner module" ref="container"
             :class="[bannerSizeClass, bannerVersionClass, {
               invisible : isEmpty && this.shouldShrink,
               offscreen
             }]"
             :style="resolvedSectionStyle">
      <div class="ad-banner__box" :style="resolvedContainerStyle">
        <div v-if="elementId" class="ad-banner__box__inner" :id="elementId" />
        <header class="ad-banner__header" :class="{'--light':!isDarkHeader,'--dark':isDarkHeader}">
          <span>{{ $t('_.ad') }}</span>
          <div class="ad-banner__close" @click="toggleHide" v-if="showCloseButton">
            <i class="icon-close" />
          </div>
        </header>
      </div>
    </section>
    <section v-else class="empty-div" ref="container"></section>
  `
    })
], AbstractAdBanner);
export { AbstractAdBanner };
